import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Alert,
  CircularProgress,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAuthToken } from "../TokenContext";
import { useSelector } from "react-redux";

const AGENT_TYPES = [
  { value: 'product_owner', label: 'Product Owner' },
  { value: 'scrum_master', label: 'Scrum Master' },
  { value: 'coder', label: 'Coder' },
  { value: 'tester', label: 'Tester' },
  { value: 'designer', label: 'Designer' },
  { value: 'devops', label: 'DevOps Engineer' },
  { value: 'support_engineer', label: 'Support Engineer' }
];
const AGENT_TYPE_DESCRIPTIONS = {
  product_owner: 'Manages product backlog, prioritizes features, and ensures alignment with business goals. Collaborates with stakeholders to define product vision and requirements.',
  scrum_master: 'Facilitates agile ceremonies, removes blockers, and ensures team follows scrum practices. Coaches team members and promotes continuous improvement.',
  coder: 'Develops and maintains code, implements features, and fixes bugs. Writes clean, efficient, and well-documented code following best practices.',
  tester: 'Creates and executes test plans, identifies defects, and ensures quality standards. Performs functional, regression, and integration testing.',
  designer: 'Creates user interfaces, designs user experiences, and maintains design systems. Ensures visual consistency and usability across products.',
  devops: 'Manages CI/CD pipelines, infrastructure automation, and deployment processes. Monitors system performance and ensures reliability.',
  support_engineer: 'Handles customer issues, troubleshoots problems, and provides technical assistance. Documents solutions and escalates complex issues.'
};
const SYSTEMS = [
  { value: 'jira', label: 'Jira' },
  { value: 'github', label: 'GitHub' },
  { value: 'gitlab', label: 'GitLab' },
  { value: 'confluence', label: 'Confluence' },
  { value: 'slack', label: 'Slack' },

  { value: 'aws', label: 'AWS' },
  { value: 'monitoring', label: 'Monitoring Tools' }
];

const PERMISSION_LEVELS = ['read', 'write', 'list', 'delete'];

const TRIGGER_TYPES = [
  { value: 'inbound_email', label: 'Inbound Email' },
  { value: 'slack_message', label: 'Slack Message' },
  { value: 'task_completion', label: 'Task Completion' },
  { value: 'jira_event', label: 'Jira Event' },
  { value: 'github_pr', label: 'GitHub Pull Request' },
  { value: 'scheduled', label: 'Scheduled (Cron)' },
  { value: 'manual', label: 'Manual Trigger' }
];

// Helper function to get trigger configuration placeholder
const getTriggerConfigPlaceholder = (triggerType) => {
  const placeholders = {
    inbound_email: 'Enter email address or pattern (e.g., support@company.com or *@company.com)',
    slack_message: 'Enter Slack channel ID or pattern (e.g., #general or C12345678)',
    task_completion: 'Enter task/ticket pattern (e.g., JIRA-* or specific task ID)',
    jira_event: 'Enter Jira project key and event types (e.g., PROJECT-KEY: issue.created, issue.updated)',
    github_pr: 'Enter repository pattern (e.g., org/repo or org/*)',
    scheduled: 'Enter cron expression (e.g., 0 9 * * 1-5 for weekdays at 9 AM)',
    manual: 'No configuration needed for manual triggers'
  };
  return placeholders[triggerType] || 'Enter trigger configuration details';
};

const CreateAgent = ({ open, onClose, onSuccess,agentToEdit  }) => {
  const [formData, setFormData] = useState({
    name: '',
    agent_type: '',
    pod_id: '',
    approver_id: '',
    trigger_type: '',
    trigger_config: '',
    metadata: {
      description: ''
    }
  });
  
  const [permissions, setPermissions] = useState([]);
  const [pods, setPods] = useState([]);
  const [podMembers, setPodMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingPods, setLoadingPods] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const { user } = useSelector(state => state.auth);
  const token = useAuthToken();
  
  useEffect(() => {
    if (open && user?.data?.[0]?.emp_id && token) {
      fetchPods();
    }
  }, [open, user, token]);

  useEffect(() => {
    if (formData.pod_id && token) {
      fetchPodMembers();
    } else {
      setPodMembers([]);
      setFormData(prev => ({ ...prev, approver_id: '' }));
    }
  }, [formData.pod_id, token]);

  const fetchPods = async () => {
    console.log('Fetching pods for agent creation...');
    if (!user?.data?.[0]?.emp_id || !token) {
      console.log('Missing emp_id or token');
      return;
    }

    setLoadingPods(true);
    try {
      const requestData = { clientID: user.data[0].emp_id };
      console.log('Request data:', requestData);

      const response = await axios.post(
        process.env.REACT_APP_CLIENT_GETMULTIPLECLIENTPODS,
        requestData,
        { headers: { Authorization: token } }
      );
      
      console.log('Pods API response:', response.data);
      
      if (response.data?.data?.podDetails) {
        console.log('Setting pods:', response.data.data.podDetails);
        setPods(response.data.data.podDetails);
      } else {
        console.log('No podDetails found in response');
        setPods([]);
      }
    } catch (err) {
      console.error('Error fetching pods:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to fetch pods');
      setPods([]);
    } finally {
      setLoadingPods(false);
    }
  };

  const fetchPodMembers = async () => {
    if (!formData.pod_id || !token) return;
    
    setLoadingMembers(true);
    try {
      console.log('Fetching members for pod:', formData.pod_id);
      
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_CLIENTFETCHPODMEMBERS,
        { pod_Id: formData.pod_id },
        { headers: { Authorization: token } }
      );
      
      console.log('Pod members API response:', response.data);
      
      if (response.data?.data) {
        const members = response.data.data.map(member => ({
          id: member.c_id,
          name: member.firstName,
          fullName: `${member.firstName} ${member.lastName || ''}`.trim()
        }));
        console.log('Mapped pod members:', members);
        setPodMembers(members);
      } else {
        console.log('No members data found in response');
        setPodMembers([]);
      }
    } catch (err) {
      console.error('Error fetching pod members:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to fetch pod members');
      setPodMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handlePermissionChange = (system, levels) => {
    setPermissions(prev => {
      const filtered = prev.filter(p => p.system !== system);
      if (levels.length > 0) {
        return [...filtered, { system, perms: levels }];
      }
      return filtered;
    });
  };

useEffect(() => {
  if (agentToEdit) {
    // Prefill form data with existing agent's data when editing
    setFormData({
      name: agentToEdit.name || '',
      agent_type: agentToEdit.agent_type || '',
      pod_id: agentToEdit.pod_id || '',
      approver_id: agentToEdit.approver_id || '',
      trigger_type: agentToEdit.trigger_type || '',
      trigger_config: agentToEdit.trigger_config || '',
      metadata: agentToEdit.metadata || { description: '' }
    });

    // TODO: If permissions data exists, initialize permissions state accordingly
    if (agentToEdit.permissions) {
      setPermissions(agentToEdit.permissions);
    } else {
      setPermissions([]);
    }
  } else {
    // Reset form for creating new agent
    setFormData({
      name: '',
      agent_type: '',
      pod_id: '',
      approver_id: '',
      trigger_type: '',
      trigger_config: '',
      metadata: { description: '' }
    });
    setPermissions([]);
  }
}, [agentToEdit]);


const handleSubmit = async () => {
  setError('');
  
  // Validation
  if (!formData.name.trim()) {
    setError('Agent name is required');
    return;
  }
  if (!formData.agent_type) {
    setError('Agent type is required');
    return;
  }
  if (!formData.pod_id) {
    setError('Pod selection is required');
    return;
  }
  if (!formData.approver_id) {
    setError('Approver is required');
    return;
  }
  if (!formData.trigger_type) {
    setError('Trigger type is required');
    return;
  }
  if (formData.trigger_type !== 'manual' && !formData.trigger_config.trim()) {
    setError('Trigger configuration is required for non-manual triggers');
    return;
  }
  if (permissions.length === 0) {
    setError('At least one permission is required');
    return;
  }

  setLoading(true);
  try {
    if (agentToEdit) {
      // Edit existing agent
      console.log('Editing agent with data:', formData);

      const response = await axios.post(
        process.env.REACT_APP_CLIENT_EDITEGENTHANDLER, // Your real edit agent endpoint
        {
          env: 'prod',
          org_id: user.data[0].client_code,
          agent_id: agentToEdit.id,
          updateData: {
            name: formData.name,
            agent_type: formData.agent_type,
            pod_id: formData.pod_id,
            approver_id: formData.approver_id,
            trigger_type: formData.trigger_type,
            trigger_config: formData.trigger_config,
            metadata: formData.metadata,
            permissions: permissions
          },
        },
        { headers: { Authorization: token } }
      );

      console.log('Agent updated successfully:', response.data);

      onSuccess && onSuccess(); // inform parent to refresh list
      onClose && onClose();
} else {
    console.log('Creating agent with data:', {
      org_id: user.data[0].client_code,
      name: formData.name,
      agent_type: formData.agent_type,
      pod_id: formData.pod_id,
      approver_id: formData.approver_id,
      trigger_type: formData.trigger_type,          // ADD THIS
      trigger_config: formData.trigger_config,      // ADD THIS
      permissions: permissions,
      metadata: formData.metadata
    });

    const response = await axios.post(
      process.env.REACT_APP_CLIENT_CREATEAGENTHANDLER,
      {
        env: 'prod',
        org_id: user.data[0].client_code,
        name: formData.name,
        agent_type: formData.agent_type,
        pod_id: formData.pod_id,
        permissions: permissions,
        approver_id: formData.approver_id,
        trigger_type: formData.trigger_type,        // ADD THIS
        trigger_config: formData.trigger_config,    // ADD THIS
        created_by: user.data[0].emp_id,
        metadata: formData.metadata
      },
      { headers: { Authorization: token } }
    );

    console.log('Agent created successfully:', response.data);
    
    if (response.data?.data?.agent) {
      onSuccess(response.data.data.agent);
      handleClose();
    } else {
      throw new Error('Invalid response format');
    }}
  } catch (err) {
    console.error('Error creating agent:', err);
    console.error('Error details:', err.response?.data);
    setError(err.response?.data?.data || err.message || 'Failed to create agent');
  } finally {
    setLoading(false);
  }
};
  const handleClose = () => {
    setFormData({
      name: '',
      agent_type: '',
      pod_id: '',
      approver_id: '',
      trigger_type: '',
      trigger_config: '',
      metadata: { description: '' }
    });
    setPermissions([]);
    setError('');
    setPodMembers([]);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon color="primary" />
          <Typography variant="h6">Create New Agent</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Agent Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Agent Name"
              placeholder="e.g., Sprint Bot, Code Reviewer"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              disabled={loading}
            />
          </Grid>

          {/* Agent Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required disabled={loading}>
              <InputLabel>Agent Type</InputLabel>
              <Select
                value={formData.agent_type}
               onChange={(e) => {
  const selectedType = e.target.value;
  handleInputChange('agent_type', selectedType);
  // Auto-populate description based on agent type
  handleInputChange('metadata.description', AGENT_TYPE_DESCRIPTIONS[selectedType] || '');
}}
                label="Agent Type"
              >
                {AGENT_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Pod Selection */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required disabled={loading || loadingPods}>
              <InputLabel>Select Pod</InputLabel>
              <Select
                value={formData.pod_id}
                onChange={(e) => handleInputChange('pod_id', e.target.value)}
                label="Select Pod"
              >
                {loadingPods ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading pods...
                  </MenuItem>
                ) : pods.length === 0 ? (
                  <MenuItem disabled>No pods available</MenuItem>
                ) : (
                  pods.map(pod => (
                    <MenuItem key={pod.id} value={pod.id}>
                      {pod.name || pod.podName || `Pod ${pod.id}`}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Approver Selection */}
          {formData.pod_id && (
            <Grid item xs={12}>
              <FormControl fullWidth required disabled={loading || loadingMembers}>
                <InputLabel>Approver (Pod Member)</InputLabel>
                <Select
                  value={formData.approver_id}
                  onChange={(e) => handleInputChange('approver_id', e.target.value)}
                  label="Approver (Pod Member)"
                >
                  {loadingMembers ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading pod members...
                    </MenuItem>
                  ) : podMembers.length === 0 ? (
                    <MenuItem disabled>No members found in this pod</MenuItem>
                  ) : (
                    podMembers.map(member => (
                      <MenuItem key={member.id} value={member.id}>
                        {member.fullName || member.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Description */}
        <Grid item xs={12}>
  <TextField
    fullWidth
    label="Description"
    placeholder="Select an agent type to see description"
    value={formData.metadata.description}
    onChange={(e) => handleInputChange('metadata.description', e.target.value)}
    multiline
    rows={3}
    disabled={true}  // Changed from {loading} to {true}
    InputProps={{
      readOnly: true,
    }}
    helperText="Description is automatically generated based on agent type"
  />
</Grid>

          {/* Trigger Configuration Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" fontWeight="600" gutterBottom sx={{ mt: 2 }}>
              Trigger Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Define when and how this agent should be activated
            </Typography>
          </Grid>

          {/* Trigger Type */}
          <Grid item xs={12}>
            <FormControl fullWidth required disabled={loading}>
              <InputLabel>Trigger Type</InputLabel>
              <Select
                value={formData.trigger_type}
                onChange={(e) => {
                  handleInputChange('trigger_type', e.target.value);
                  // Clear trigger config when changing type
                  handleInputChange('trigger_config', '');
                }}
                label="Trigger Type"
              >
                {TRIGGER_TYPES.map(trigger => (
                  <MenuItem key={trigger.value} value={trigger.value}>
                    {trigger.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Trigger Configuration Details */}
          {formData.trigger_type && formData.trigger_type !== 'manual' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trigger Configuration"
                placeholder={getTriggerConfigPlaceholder(formData.trigger_type)}
                value={formData.trigger_config}
                onChange={(e) => handleInputChange('trigger_config', e.target.value)}
                multiline
                rows={3}
                disabled={loading}
                required
                helperText={`Specify the configuration for ${TRIGGER_TYPES.find(t => t.value === formData.trigger_type)?.label}`}
              />
            </Grid>
          )}

          {formData.trigger_type === 'manual' && (
            <Grid item xs={12}>
              <Alert severity="info">
                Manual trigger: This agent will only run when explicitly triggered by a user.
              </Alert>
            </Grid>
          )}

          {/* System Permissions Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" fontWeight="600" gutterBottom sx={{ mt: 2 }}>
              System Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select systems and permission levels for this agent
            </Typography>
          </Grid>

          {/* Permissions */}
          {SYSTEMS.map(system => (
            <Grid item xs={12} key={system.value}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{system.label}</InputLabel>
                  <Select
                    multiple
                    value={permissions.find(p => p.system === system.value)?.perms || []}
                    onChange={(e) => handlePermissionChange(system.value, e.target.value)}
                    input={<OutlinedInput label={system.label} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip 
                            key={value} 
                            label={value} 
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                    disabled={loading}
                  >
                    {PERMISSION_LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>
                        <Checkbox 
                          checked={(permissions.find(p => p.system === system.value)?.perms || []).includes(level)}
                        />
                        <ListItemText primary={level.charAt(0).toUpperCase() + level.slice(1)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Grid>
          ))}

          {/* Selected Permissions Summary */}
          {permissions.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Selected Permissions Summary:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {permissions.map(perm => (
                    <Chip
                      key={perm.system}
                      label={`${SYSTEMS.find(s => s.value === perm.system)?.label}: ${perm.perms.join(', ')}`}
                      color="primary"
                      size="small"
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.pod_id || !formData.trigger_type}
          startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {loading ? 'Creating...' : 'Create Agent'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAgent;